import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useTranslation } from "react-i18next";
import {
  areAllOrderExpenseReviewItemsChecked,
  isOrderApproved,
  OrderStatus,
  type Order,
} from "../../types/order";
import DisabledButtonTooltip from "../common/DisabledButtonTooltip";
import DetailSectionCard from "../common/DetailSectionCard";
import OrderDetailPanel from "../orders/OrderDetailPanel";
import AddOrdersToReportDialog from "./AddOrdersToReportDialog";
import ApproveOverrideDialog from "./ApproveOverrideDialog";
import ReportOrderCardFinanceActionsMenu from "./ReportOrderCardFinanceActionsMenu";
import { createReportCardFinanceActions } from "./reportCardFinanceActions";
import { UangJalanReportStatus, type UangJalanReport } from "../../types/uangJalanReport";
import type translations from "../../translations/translations.json";
import { TestId } from "../../constants/testIds";
import { canMutateOrders, isFinanceApprover, isFinance, isUserAdmin } from "../../types/user";
import type { User } from "../../types/user";
import { verifyUangJalanReportApi } from "../../api/uangJalanReportsApi";
import {
  approveOrderApi,
  requestRevisionOrderApi,
  updateExpenseReviewChecklistApi,
  verifyOrderApi,
} from "../../api/ordersApi";
import { toastEmitter } from "../../utils/toastEmitter";
import {
  cascadeReportVerifyIfReady,
  emitOrderAutoVerifiedToast,
  runAutoVerifyAfterChecklistComplete,
} from "../../utils/uangJalanReportAutoVerify";

type TranslationKeys = keyof typeof translations.en;

interface UangJalanReportOrdersSectionProps {
  report: UangJalanReport;
  user: User | null;
  onReportChange: () => Promise<UangJalanReport | null>;
  onOrderUpdated?: (order: Order) => void;
  onActionError: (message: string) => void;
}

const UangJalanReportOrdersSection: React.FC<UangJalanReportOrdersSectionProps> = ({
  report,
  user,
  onReportChange,
  onOrderUpdated,
  onActionError,
}) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState(false);
  const [checklistLoadingOrderId, setChecklistLoadingOrderId] = useState<string | null>(null);
  const [reviseDialogOrderId, setReviseDialogOrderId] = useState<string | null>(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [addOrdersDialogOpen, setAddOrdersDialogOpen] = useState(false);
  const [approveOverrideOrder, setApproveOverrideOrder] = useState<Order | null>(null);

  const orders = report.orders ?? [];
  const userCanMutate = !!user && canMutateOrders(user);
  const userCanFinance = !!user && (isFinance(user) || isUserAdmin(user));
  const userCanApprove = !!user && (isFinanceApprover(user) || isUserAdmin(user));
  const reportIsOpen = report.status === UangJalanReportStatus.OPEN;
  const reportInReview = report.status === UangJalanReportStatus.IN_REVIEW;
  const reportIsVerified = report.status === UangJalanReportStatus.VERIFIED;
  const checklistEnabled = userCanFinance && reportInReview;
  const reportBlocksRevision =
    report.status === UangJalanReportStatus.VERIFIED ||
    report.status === UangJalanReportStatus.APPROVED ||
    report.status === UangJalanReportStatus.APPROVED_WITH_OVERRIDE;

  const canAddOrders = userCanMutate && reportIsOpen;
  const addOrdersDisabledHint = !userCanMutate
    ? t("reports.addOrdersDisabledRole" as TranslationKeys)
    : !reportIsOpen
      ? t("reports.addOrdersDisabledHint" as TranslationKeys)
      : "";

  const getVerifyOrderDisabledHint = (order: Order, allChecklistComplete: boolean): string => {
    if (!userCanFinance) {
      return t("permission.financeOnly" as TranslationKeys);
    }
    if (!reportInReview) {
      return t("reports.financeActionsDisabledStatus" as TranslationKeys);
    }
    if (order.status !== OrderStatus.IN_REVIEW) {
      return t("reports.verifyOrderDisabledStatus" as TranslationKeys);
    }
    if (!allChecklistComplete) {
      return t("reports.verifyOrderDisabledHint" as TranslationKeys);
    }
    return "";
  };

  const getApproveOrderDisabledHint = (order: Order): string => {
    if (!userCanApprove) {
      return t("permission.approverOnly" as TranslationKeys);
    }
    if (!reportIsVerified) {
      return t("reports.approveOrderDisabledReportStatus" as TranslationKeys);
    }
    if (order.status !== OrderStatus.VERIFIED) {
      return t("reports.approveOrderDisabledStatus" as TranslationKeys);
    }
    return "";
  };

  const getReviseOrderDisabledHint = (order: Order): string => {
    if (!userCanFinance) {
      return t("permission.financeOnly" as TranslationKeys);
    }
    if (reportBlocksRevision) {
      return t("reports.reviseOrderDisabledReportStatus" as TranslationKeys);
    }
    if (order.status !== OrderStatus.IN_REVIEW && order.status !== OrderStatus.VERIFIED) {
      return t("reports.reviseOrderDisabledStatus" as TranslationKeys);
    }
    return "";
  };

  const runOrderAction = async (action: () => Promise<Order>, successMessage: TranslationKeys) => {
    setActionLoading(true);
    try {
      await action();
      let refreshedReport = await onReportChange();
      if (successMessage === "orders.verifiedSuccessfully") {
        refreshedReport = await cascadeReportVerifyIfReady(
          refreshedReport,
          verifyUangJalanReportApi,
          onReportChange,
        );
        emitOrderAutoVerifiedToast(t, refreshedReport);
      } else {
        toastEmitter.emit(t(successMessage), "success");
      }
    } catch (err: any) {
      onActionError(err?.response?.data?.error?.message || err.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChecklistToggle = async (orderId: string, itemKey: string, checked: boolean) => {
    setChecklistLoadingOrderId(orderId);
    try {
      const updatedOrder = await updateExpenseReviewChecklistApi(orderId, itemKey, checked);
      const refreshedReport = await runAutoVerifyAfterChecklistComplete({
        order: updatedOrder,
        checked,
        isChecklistComplete: (member) => areAllOrderExpenseReviewItemsChecked(member as Order),
        verifyMember: verifyOrderApi,
        refreshReport: onReportChange,
        verifyReport: verifyUangJalanReportApi,
      });
      if (refreshedReport !== null) {
        emitOrderAutoVerifiedToast(t, refreshedReport);
      } else {
        onOrderUpdated?.(updatedOrder);
      }
    } catch (err: any) {
      onActionError(err?.response?.data?.error?.message || err.message || "Action failed");
    } finally {
      setChecklistLoadingOrderId(null);
    }
  };

  const handleRevise = async () => {
    if (!reviseDialogOrderId) {
      return;
    }

    const trimmedNote = revisionNote.trim();
    if (!trimmedNote) {
      return;
    }

    await runOrderAction(
      () => requestRevisionOrderApi(reviseDialogOrderId, trimmedNote),
      "orders.revisionRequestedSuccessfully",
    );
    setReviseDialogOrderId(null);
    setRevisionNote("");
  };

  const revisionNoteTrimmed = revisionNote.trim();

  const verifiedCount = orders.filter(
    (order) => order.status === OrderStatus.VERIFIED || isOrderApproved(order.status),
  ).length;

  return (
    <Box sx={{ mt: 3 }}>
      <DetailSectionCard
        title={t("reports.ordersSection" as TranslationKeys)}
        icon={<LocalShippingOutlinedIcon fontSize="small" />}
        iconColor="#64748B"
        sx={{ mb: 0.75 }}
        headerMeta={
          <Typography variant="body2" color="text.secondary">
            {t("reports.ordersSectionMeta" as TranslationKeys, {
              count: orders.length,
              verified: verifiedCount,
            })}
          </Typography>
        }
        headerActions={
          <DisabledButtonTooltip title={addOrdersDisabledHint} disabled={!canAddOrders}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              disabled={!canAddOrders}
              onClick={() => setAddOrdersDialogOpen(true)}
              data-testid={TestId.UJR.ADD_ORDERS}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              {t("reports.addOrders" as TranslationKeys)}
            </Button>
          </DisabledButtonTooltip>
        }
      />

      <Stack spacing={1}>
        {orders.map((order) => {
          const allChecklistComplete = areAllOrderExpenseReviewItemsChecked(order);
          const canVerifyOrder =
            userCanFinance &&
            reportInReview &&
            order.status === OrderStatus.IN_REVIEW &&
            allChecklistComplete;
          const canReviseOrder =
            userCanFinance &&
            !reportBlocksRevision &&
            (order.status === OrderStatus.IN_REVIEW || order.status === OrderStatus.VERIFIED);
          const verifyDisabledHint = getVerifyOrderDisabledHint(order, allChecklistComplete);
          const reviseDisabledHint = getReviseOrderDisabledHint(order);
          const canApproveOrder =
            userCanApprove && reportIsVerified && order.status === OrderStatus.VERIFIED;
          const approveDisabledHint = getApproveOrderDisabledHint(order);

          const financeActions = (
            <ReportOrderCardFinanceActionsMenu
              menuTestId={TestId.UJR.ORDER_CARD_ACTIONS_MENU(order._id)}
              menuId={`ujr-order-card-actions-menu-${order._id}`}
              ariaLabel={t("Actions" as TranslationKeys)}
              actions={createReportCardFinanceActions({
                t,
                actionLoading,
                userCanApprove,
                canVerify: canVerifyOrder,
                canRevise: canReviseOrder,
                canApprove: canApproveOrder,
                verifyDisabledHint,
                reviseDisabledHint,
                approveDisabledHint,
                onVerify: () =>
                  runOrderAction(() => verifyOrderApi(order._id), "orders.verifiedSuccessfully"),
                onRevise: () => setReviseDialogOrderId(order._id),
                onApprove: () =>
                  runOrderAction(() => approveOrderApi(order._id), "orders.approvedSuccessfully"),
                onApproveOverride: () => setApproveOverrideOrder(order),
                testIds: {
                  verify: TestId.UJR.VERIFY_ORDER(order._id),
                  revise: TestId.UJR.REVISE_ORDER(order._id),
                  approve: TestId.UJR.APPROVE_ORDER(order._id),
                  approveOverride: TestId.UJR.APPROVE_OVERRIDE_ORDER(order._id),
                },
              })}
            />
          );

          return (
            <OrderDetailPanel
              key={order._id}
              order={order}
              variant="card"
              rootTestId={TestId.UJR.ORDER_ROW}
              checklist={{
                disabled: !checklistEnabled || order.status !== OrderStatus.IN_REVIEW,
                onToggle: handleChecklistToggle,
                loading: checklistLoadingOrderId === order._id,
              }}
              secondaryLink={{ type: "order-detail", openInNewTab: true }}
              financeActions={financeActions}
            />
          );
        })}
      </Stack>

      {userCanMutate && (
        <AddOrdersToReportDialog
          report={report}
          open={addOrdersDialogOpen}
          onClose={() => setAddOrdersDialogOpen(false)}
          onSuccess={async () => {
            await onReportChange();
          }}
        />
      )}

      {userCanApprove && (
        <ApproveOverrideDialog
          order={approveOverrideOrder}
          open={approveOverrideOrder !== null}
          onClose={() => setApproveOverrideOrder(null)}
          onSuccess={async () => {
            await onReportChange();
          }}
        />
      )}

      <Dialog
        open={reviseDialogOrderId !== null}
        onClose={() => {
          setReviseDialogOrderId(null);
          setRevisionNote("");
        }}
        fullWidth
        maxWidth="sm"
        data-testid={TestId.UJR.REVISION_DIALOG}
      >
        <DialogTitle>{t("orders.requestRevision" as TranslationKeys)}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("reports.revisionDialogHint" as TranslationKeys)}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            required
            label={t("orders.revisionNote" as TranslationKeys)}
            value={revisionNote}
            onChange={(event) => setRevisionNote(event.target.value)}
            inputProps={{ "data-testid": TestId.UJR.REVISION_NOTE }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setReviseDialogOrderId(null);
              setRevisionNote("");
            }}
          >
            {t("Cancel" as TranslationKeys)}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={actionLoading || !revisionNoteTrimmed}
            onClick={handleRevise}
            data-testid={TestId.UJR.REVISION_SUBMIT}
          >
            {t("orders.requestRevision" as TranslationKeys)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UangJalanReportOrdersSection;
